import React, { FC, memo, useCallback, useEffect, useState } from "react";
import { parse } from "query-string";
import { navigate } from "@reach/router";
import { IconButton, useToggle } from "@evergis/ui";
import { RegisterUserDc } from "@evergis/api";

import { FormInputs } from "./FormInputs";
import { FormFooter } from "./FormFooter";
import { SignInFormContainer, SignInFormHeader, SignInTitle } from "../sign-in/styled";

import { ROUTES } from "../../appConfig";

import { checkLogin, signUp } from "api/user";
import { getResponseError } from "../../utils/user";
import { enhance } from "./enhance";
import { useRegFormSettings } from "../../hooks/useRegFormSettings";

import { ISignUpProps } from "./types";
import { ValidationErrorPayload } from "../../api/types";
import { UserFormError } from "../../ducks/user";
import { ValidationError } from "../../api";

// eslint-disable-next-line max-lines-per-function
const SignUpComponent: FC<ISignUpProps> = ({ values, touched, errors, saveEmailToState, handleChange, handleBlur }) => {
  const { email, username, password1, phone, name, familyName, surname, company } = values || {};
  const [isUsernameUnique, setIsUsernameUnique] = useState<boolean | undefined>(undefined);
  const [isEmailUnique, setIsEmailUnique] = useState<boolean | undefined>(undefined);
  const [isWaiting, setIsWaiting] = useState(false);
  const [isSubscribed, toggleSubscribed] = useToggle(true);
  const [isTermsAccepted, toggleTermsAccepted] = useToggle(true);
  const [error, setError] = useState<Partial<UserFormError>>({
    email: "",
    none_field_errors: "",
    other: "",
  });

  const { title } = useRegFormSettings();

  useEffect(() => {
    addEventListener("keydown", sendOnEnter);

    return () => {
      removeEventListener("keydown", sendOnEnter);
    };
  }, []);

  const send = useCallback(() => {
    checkLogin({ username: username as string })
      .then((isExist) => {
        if (!isExist) {
          setIsWaiting(true);
          setIsUsernameUnique(true);
          // Todo update evergis/api for actual type interface
          signUp({
            email,
            username,
            password: password1,
            phone,
            first_name: name,
            last_name: familyName,
            patronymic: surname,
            company,
            is_subscribed: isSubscribed,
          } as RegisterUserDc)
            .then(() => {
              saveEmailToState && saveEmailToState(email as string);
              navigate((parse(window.location.search).next as string) || ROUTES.SIGN_UP_SUCCESS);
            })
            .catch(async (checkError: ValidationError | Error) => {
              const newPayload = await getResponseError(checkError);
              const newError = { other: newPayload };

              setError(newError as ValidationErrorPayload<UserFormError>);
            })
            .finally(() => {
              setIsWaiting(false);
            });
        }
      })
      .catch((sendError) => console.error(sendError.message));
  }, [
    username,
    email,
    username,
    password1,
    phone,
    name,
    familyName,
    surname,
    company,
    isSubscribed,
    saveEmailToState,
    window.location.search,
  ]);

  const sendOnEnter = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        send();
      }
    },
    [send],
  );

  const goBack = useCallback(() => {
    navigate(ROUTES.SIGN_IN);
  }, [navigate]);

  return (
    <SignInFormContainer style={{ justifyContent: "flex-start" }}>
      <SignInFormHeader>
        <SignInTitle>{title}</SignInTitle>
        <span style={{ marginRight: "-0.5rem" }}>
          <IconButton kind={"close"} onClick={goBack} />
        </span>
      </SignInFormHeader>
      <FormInputs
        values={values}
        errors={errors}
        touched={touched}
        isUsernameUnique={isUsernameUnique}
        setIsUsernameUnique={setIsUsernameUnique}
        isEmailUnique={isEmailUnique}
        setIsEmailUnique={setIsEmailUnique}
        handleChange={handleChange}
        handleBlur={handleBlur}
      />
      <FormFooter
        values={values}
        error={error}
        errors={errors}
        isUsernameUnique={isUsernameUnique}
        isEmailUnique={isEmailUnique}
        isWaiting={isWaiting}
        isSubscribed={isSubscribed}
        toggleSubscribed={toggleSubscribed}
        isTermsAccepted={isTermsAccepted}
        toggleTermsAccepted={toggleTermsAccepted}
        send={send}
      />
    </SignInFormContainer>
  );
};

export const Form = enhance(memo(SignUpComponent));
